import { ISuppressionList, SuppressionListScope } from '../interfaces/models/campaign';

type ScopeOption = { scope: SuppressionListScope; label: string; description: string };

/** The three scopes as the create/edit form offers them. */
export const SUPPRESSION_SCOPE_OPTIONS: ScopeOption[] = [
  {
    scope: 'all',
    label: 'Every campaign',
    description: 'Nobody in the workspace contacts them.',
  },
  {
    scope: 'others',
    label: 'Reserved for me',
    description: 'Only your campaigns can contact them. Everyone else’s skip them.',
  },
  {
    scope: 'selected',
    label: 'Selected campaigns',
    description: 'Applies only to campaigns where you turn it on.',
  },
];

/**
 * The options for a list owned by someone else (an admin editing it): a reserved list
 * keeps its owner, so "Reserved for me" would say the wrong person.
 */
export function scopeOptionsFor(ownerName?: string): ScopeOption[] {
  if (!ownerName) return SUPPRESSION_SCOPE_OPTIONS;
  return SUPPRESSION_SCOPE_OPTIONS.map(option => (option.scope === 'others'
    ? {
      ...option,
      label: `Reserved for ${ownerName}`,
      description: `Only ${ownerName}’s campaigns can contact them. Everyone else’s skip them.`,
    }
    : option));
}

/** Which campaigns a list binds, said from the viewer's side. */
export function describeListScope(list: Pick<ISuppressionList, 'scope' | 'owner'>, userId?: string): string {
  if (list.scope === 'all') return 'Every campaign';
  if (list.scope === 'others') {
    return list.owner && String(list.owner._id) === String(userId)
      ? 'Reserved for you — only your campaigns can contact them'
      : `Reserved for ${list.owner?.name || 'a member'} — only their campaigns can contact them`;
  }
  return 'Selected campaigns — only where it’s turned on';
}

/** Whether a list binds a campaign — the API's rule (ContactSuppressionService.appliesTo). */
export function listAppliesTo(
  list: Pick<ISuppressionList, '_id' | 'scope' | 'owner'>,
  campaign: { createdBy?: string; suppressionListIds?: string[] },
): boolean {
  if (list.scope === 'all') return true;
  if (list.scope === 'others') return String(list.owner?._id) !== String(campaign.createdBy);
  return (campaign.suppressionListIds || []).some(id => String(id) === String(list._id));
}
